import type { Chat, KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import {
  type FileNode,
  type FileSystemNode,
  type FolderNode,
  type NodeCreator,
} from '../../types/fileSystem.js';
import { isUnprocessedStatus } from '../ragDocumentStatus.js';

export const isFolderNode = (
  node?: FileSystemNode | null,
): node is FolderNode => node?.kind === 'folder';

export const isUnprocessedFile = (
  node?: FileSystemNode | null,
): node is FileNode =>
  node?.kind === 'file' && isUnprocessedStatus(node.status);

export const getNodeName = (node: FileSystemNode) =>
  isFolderNode(node) ? node.name : node.fileName;

export const getNodeCreatedAt = (node: FileSystemNode) =>
  isFolderNode(node) ? node.createdAt : node.uploadedDate;

export const getNodeCreator = (
  node: FileSystemNode,
): NodeCreator | undefined => (isFolderNode(node) ? node.owner : node);

/** Names are unique among siblings, ignoring case and surrounding spaces. */
export const hasSiblingNamed = (
  siblings: FolderNode[],
  name: string,
  excludeId?: string,
) => {
  const wanted = name.trim().toLowerCase();

  return siblings.some(
    (folder) =>
      folder.id !== excludeId && folder.name.trim().toLowerCase() === wanted,
  );
};

export const getNodeRowId = (node: FileSystemNode) => `${node.kind}:${node.id}`;

export const PENDING_FOLDER_ID_PREFIX = 'pending-folder:';

export const isPendingFolder = (node?: FileSystemNode | null) =>
  isFolderNode(node) && node.id.startsWith(PENDING_FOLDER_ID_PREFIX);

export const toFolderNode = (
  folder: KnowledgeBase.Folder,
  fallbackParentId: string,
): FolderNode => ({
  kind: 'folder',
  id: folder.folderId,
  name: folder.name,
  description: folder.description ?? undefined,
  isPrivate: folder.isPrivate ?? undefined,
  parentId: folder.parentFolderId ?? fallbackParentId,
  isBucket: folder.isBucket ?? undefined,
  isSystem: folder.isSystem ?? undefined,
  createdAt: folder.createdAt ?? undefined,
  owner: folder.owner ?? undefined,
  hasChildren: folder.hasChildren ?? false,
});

/** Serves both sources: a listing file is a rag document plus its folder. */
export const toFileNode = (document: Chat.RagDocumentType): FileNode => ({
  kind: 'file',
  id: document.fileId,
  fileId: document.fileId,
  fileName: document.filename,
  fileType: document.type,
  description: document.description,
  uploadedDate: document.createdAt,
  firstName: document.firstName,
  lastName: document.lastName,
  userId: document.userId,
  status: document.status,
});

export const splitFileSystemNodes = <T extends FileSystemNode>(nodes: T[]) => {
  const folders = nodes.filter((node): node is Extract<T, FolderNode> =>
    isFolderNode(node),
  );

  const files = nodes.filter(
    (node): node is Extract<T, FileNode> => !isFolderNode(node),
  );

  return {
    folders,
    files,
    foldersCount: folders.length,
    filesCount: files.length,
  };
};
